import { Badge, Card, Container, Button } from "reactstrap";
import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import SupportalInviteUserDrawer from "../../components/Drawers/SupportalInviteUserDrawer/SupportalInviteUserDrawer";
import useUpdateUserCloudPermissions from "../../api/api-hooks/auth/useUpdateUserCloudPermissions";
import { useUserPermissionsTenant } from "../../api/api-hooks/supportal/useUserPermissionsTenant";
import ManageUserPermissionsDrawer from "../../components/Drawers/ManageUserPermissionsDrawer";
import { useCloudsProfiles } from "../../api/api-hooks/supportal/useCloudProfiles";
import { usePersonSupportal } from "../../api/api-hooks/supportal/usePerson";
import useRemoveUser from "../../api/api-hooks/tenantUsers/useRemoveUser";
import { PeopleWithInfo } from "../../api/api-hooks/cloud/useCloud";
import { getUserCloudPermissionID } from "../../utils/UserUtils";
import SupportalUserHeader from "./Users/SupportalUserHeader";
import BasicTableView from "./CloudListView/BasicTableView";
import CollapseCard from "./Components/CollapseCard";
import IconButton from "../../elements/IconButton";
import StrUtils from "../../utils/StrUtils";

const PersonDetailView = ({ urlStart }: { urlStart?: string }) => {
  const { personID }: any = useParams<{ personID: string }>();
  const { person, personLoading: loading } = usePersonSupportal(
    personID || null
  );
  const tenants = useMemo(
    () => person?.Tenants.filter((tenant: any) => tenant.TenantID !== "GLOBAL"),
    [person]
  );
  const [permissionsDrawer, setDrawer] = useState<{
    open: boolean;
    tenantID: string | undefined;
  }>({
    open: false,
    tenantID: undefined,
  });
  const [inviteDrawer, setInviteDrawer] = useState(false);
  const queryClient = useQueryClient();
  const { removeUser } = useRemoveUser();
  const { permissionsFromTenant } = useUserPermissionsTenant(
    person?.id,
    permissionsDrawer?.tenantID
  );
  const {
    updateUserCloudPermissions,
    updatingPermissions,
    updatePermissionsError,
  } = useUpdateUserCloudPermissions();
  const { personCredentials: credentials, credentialsLoading } =
    usePersonSupportal(person?.id);
  const { profilesFromTenant, profilesLoading } = useCloudsProfiles(
    tenants,
    personID
  );
  const openPermissionsDrawer = (cloud: any) => {
    setDrawer({ open: true, tenantID: cloud.TenantID });
  };
  urlStart = urlStart || "/supportal";

  async function removeUserFromCloud(user: PeopleWithInfo, cloudID: string) {
    removeUser({
      user,
      supportTargetCloudID: cloudID, // Support Request - Provide Cloud ID!
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["person", user?.id],
        });
      },
    });
  }

  const handleGetPermissionID = useCallback(
    (user: any) => {
      return getUserCloudPermissionID(user, permissionsDrawer.tenantID);
    },
    [permissionsDrawer]
  );

  const tenantsPermissions = useMemo(() => {
    if (!tenants || !profilesFromTenant || profilesLoading) return;

    return tenants?.map((tenant: any) => {
      const pFound = profilesFromTenant?.find(
        (p: any) => p?.TenantID === tenant?.TenantID
      );
      const currentPermissions =
        pFound?.profile?.filter(
          (p: any) =>
            p?.id === getUserCloudPermissionID(person, tenant.TenantID)
        ) || [];

      return {
        Permissions: currentPermissions[0] || "",
        TenantID: tenant.TenantID,
      };
    });
  }, [tenants, profilesFromTenant, profilesLoading, person?.Tenants]);

  const mappedTenants = useMemo(() => {
    return tenants?.map((tenant: any) => {
      return {
        ...tenant,
        BadgeText: tenantsPermissions?.find(
          (e: any) => e.TenantID === tenant.TenantID
        ).Permissions?.Name,
      };
    });
  }, [tenants, tenantsPermissions]);

  return (
    <>
      <SupportalUserHeader person={person} urlStart={urlStart} />
      <Container fluid>
        <hr className="mt-0 mb-4"></hr>
        <Card className="shadow tw:p-3">
          <h3 className="tw:font-800 ">Contact Information</h3>
          <div className=" tw:flex tw:items-center">
            <h5 className="tw:p-0 tw:m-0">Name:</h5>
            <p className="tw:p-0 tw:m-0 tw:ml-2 tw:text-sm">{person?.Name}</p>
          </div>
          <div className=" tw:flex tw:items-center">
            <h5 className="tw:p-0 tw:m-0">Email:</h5>
            <p className="tw:p-0 tw:m-0 tw:ml-2 tw:text-sm">
              {person?.PrimaryContactEmail}
            </p>
            {!person ? null : (
              <div className="tw:ml-2">
                {person?.PrimaryContactEmailVerified ? (
                  <Badge color="success">Verified</Badge>
                ) : (
                  <Badge color="warning">Not Verified</Badge>
                )}
              </div>
            )}
          </div>
          <div className=" tw:flex tw:items-center">
            <h5 className="tw:p-0 tw:m-0">Phone:</h5>
            <p className="tw:p-0 tw:m-0 tw:ml-2 tw:text-sm">
              {person?.Phone ? StrUtils.formatPhone(person.Phone) : "--"}
            </p>
          </div>
        </Card>

        <CollapseCard
          title="Clouds"
          subtitle="Manage User's cloud permissions"
          startOpen
          options={
            <Button
              color="primary"
              size="sm"
              onClick={() => setInviteDrawer(true)}
            >
              Add Cloud
            </Button>
          }
        >
          <BasicTableView
            loading={loading}
            items={mappedTenants}
            title="Clouds"
            getItemTitle={(tenant: any) => {
              return (
                <div className="d-flex align-items-center">
                  <h4 className="m-0">{tenant.TenantName}</h4>
                  <Badge
                    color={tenant.TenantOwner ? "secondary" : "info"}
                    className="ml-2"
                  >
                    {tenant.BadgeText}
                  </Badge>
                </div>
              );
            }}
            getItemSubtitle={() => ""}
            onClick={undefined}
            CustomRenderer={(cloud) => {
              return (
                <div className="d-flex" onClick={(e) => e.stopPropagation()}>
                  <IconButton
                    icon="pencil"
                    tooltipText={"Edit user's permissions"}
                    onClick={() => openPermissionsDrawer(cloud)}
                  />
                  <IconButton
                    icon="close"
                    color="danger"
                    tooltipText={"Remove user from Tenant"}
                    onClick={() => {
                      removeUserFromCloud(person, cloud.TenantID);
                    }}
                  />
                </div>
              );
            }}
          />
        </CollapseCard>

        <BasicTableView
          loading={credentialsLoading}
          items={credentials}
          title="Credentials"
          onClick={undefined}
          titleCSS="tw:rounded-t-md tw:px-2"
          getItemTitle={(credential: any) => credential.Provider}
          getItemSubtitle={(credentials) => credentials.Email}
        />
        <ManageUserPermissionsDrawer
          open={permissionsDrawer.open}
          selectedUser={person}
          permissions={permissionsFromTenant}
          onClose={() => setDrawer({ open: false, tenantID: undefined })}
          type="Cloud"
          getPermissionID={handleGetPermissionID}
          updatePermissions={updateUserCloudPermissions}
          updatingPermissions={updatingPermissions}
          updatePermissionsError={updatePermissionsError}
          onSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: ["person", person.id],
            });
          }}
          supportTargetCloudID={permissionsDrawer?.tenantID!} // Support Request - Provide Cloud ID!
        />
        {!loading && (
          <SupportalInviteUserDrawer
            open={inviteDrawer}
            onClose={() => setInviteDrawer(false)}
            person={person}
          />
        )}
      </Container>
    </>
  );
};

export default PersonDetailView;
